import { useId, useRef, useState, type KeyboardEvent } from "react";
import * as Popover from "@radix-ui/react-popover";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { addDays, addMonths, startOfMonth, startOfWeek } from "date-fns";
import { calendarDate, dateInRange, isoDate, parseCalendarInput, periodDate, type CalendarMode, type DateBoundary } from "@/lib/calendar";
import { errorMessage } from "@/lib/api";
import { PropertyChip } from "./PropertyChip";

type DatePickerProps = {
  value?: string | null;
  onChange: (value: string) => void | Promise<unknown>;
  label: string;
  boundary?: DateBoundary;
  min?: string | null;
  max?: string | null;
  disabled?: boolean;
  required?: boolean;
  compact?: boolean;
  className?: string;
  displayText?: string;
};

export function DatePicker(props: DatePickerProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const date = props.value ? calendarDate(props.value) : null;
  const text = props.displayText ?? (date ? date.toLocaleDateString(i18n.language, { year: "numeric", month: "short", day: "numeric" }) : props.label);
  return <Popover.Root open={open} onOpenChange={setOpen}>
    <Popover.Trigger asChild>
      <PropertyChip disabled={props.disabled} className={props.className} title={props.label} aria-label={`${props.label}: ${text}`}>
        <CalendarDays className="h-3.5 w-3.5 shrink-0" />
        {(!props.compact || props.value) && <span className="truncate">{text}</span>}
      </PropertyChip>
    </Popover.Trigger>
    <Popover.Portal>
      {open && <CalendarPopover {...props} close={() => setOpen(false)} />}
    </Popover.Portal>
  </Popover.Root>;
}

function CalendarPopover({ value, onChange, label, boundary = "end", min, max, required, close }: DatePickerProps & { close: () => void }) {
  const { t, i18n } = useTranslation();
  const inputId = useId();
  const gridRef = useRef<HTMLDivElement>(null);
  const initial = (value && calendarDate(value)) || new Date();
  const [month, setMonth] = useState(startOfMonth(initial));
  const [focusDay, setFocusDay] = useState(isoDate(initial));
  const [input, setInput] = useState(value ?? "");
  const [mode, setMode] = useState<CalendarMode>("day");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const today = isoDate(new Date());
  const year = month.getFullYear();
  const allowed = (date: string) => dateInRange(date, min, max);
  const format = (date: Date, options: Intl.DateTimeFormatOptions) => date.toLocaleDateString(i18n.language, options);
  const select = async (date: string) => {
    if (saving.current) return;
    if (date && !allowed(date)) { setError(t("calendar.outOfRange", { min: min ?? "—", max: max ?? "—" })); return; }
    saving.current = true;
    setBusy(true);
    try { await onChange(date); close(); }
    catch (e) { setError(errorMessage(e)); }
    finally { saving.current = false; setBusy(false); }
  };
  const applyInput = () => {
    const parsed = parseCalendarInput(input, boundary);
    if (!parsed) { setError(t("calendar.invalidDate")); return; }
    void select(parsed);
  };
  const moveFocus = (event: KeyboardEvent<HTMLButtonElement>, date: Date) => {
    const offsets: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -((date.getDay() + 6) % 7), End: 6 - ((date.getDay() + 6) % 7) };
    const next = event.key === "PageUp" ? addMonths(date, -1) : event.key === "PageDown" ? addMonths(date, 1) : event.key in offsets ? addDays(date, offsets[event.key]!) : null;
    if (!next) return;
    event.preventDefault();
    const iso = isoDate(next);
    setFocusDay(iso);
    setMonth(startOfMonth(next));
    requestAnimationFrame(() => gridRef.current?.querySelector<HTMLButtonElement>(`[data-date="${iso}"]`)?.focus());
  };
  const first = startOfWeek(month, { weekStartsOn: 1 });
  const periods = mode === "month" ? 12 : mode === "quarter" ? 4 : mode === "half" ? 2 : 1;
  const months = 12 / periods;
  return <Popover.Content align="start" sideOffset={8} collisionPadding={12} aria-label={label}
    className="z-[60] w-[336px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-2xl border border-border-strong bg-elevated shadow-2xl max-h-[var(--radix-popover-content-available-height)]"
    onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
    <fieldset disabled={busy} className="min-w-0">
      <div className="p-4 pb-3">
        <label htmlFor={inputId} className="mb-2 block text-[12px] font-medium text-muted">{label}</label>
        <input id={inputId} className="input h-10 w-full text-[13px]" placeholder={t("calendar.placeholder")} value={input} aria-invalid={!!error} aria-describedby={error ? `${inputId}-error` : undefined}
          onChange={(e) => { setInput(e.target.value); setError(""); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyInput(); } }} />
        {error && <p id={`${inputId}-error`} role="alert" className="mt-2 text-[12px] text-danger">{error}</p>}
        <div className="mt-3 flex gap-1" role="group" aria-label={t("calendar.precision")}>
          {(["day", "month", "quarter", "half", "year"] as const).map((item) => <button key={item} type="button" aria-pressed={mode === item} onClick={() => setMode(item)} className={`h-7 flex-1 rounded-full border px-1 text-[11.5px] ${mode === item ? "border-accent/50 bg-accent/10 text-accent" : "border-border text-muted hover:bg-hover"}`}>{t(`calendar.${item}`)}</button>)}
        </div>
      </div>
      <div className="border-t border-border p-3">
        <div className="mb-3 flex items-center justify-between px-1">
          <span className="text-[13px] font-medium">{mode === "day" ? format(month, { year: "numeric", month: "long" }) : year}</span>
          <div className="flex gap-1">
            {([-1, 1] as const).map((direction) => <button key={direction} type="button" aria-label={t(direction === -1 ? "calendar.previous" : "calendar.next")} className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-hover" onClick={() => { const next = addMonths(month, direction * (mode === "day" ? 1 : 12)); setMonth(next); setFocusDay(isoDate(next)); }}>{direction === -1 ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>)}
          </div>
        </div>
        {mode === "day" ? <>
          <div className="mb-1 grid grid-cols-7 text-center text-[11px] text-faint">{Array.from({ length: 7 }, (_, i) => <span key={i} className="py-1">{format(addDays(first, i), { weekday: "short" })}</span>)}</div>
          <div ref={gridRef} className="grid grid-cols-7 gap-y-1" role="group" aria-label={t("calendar.day")}>
            {Array.from({ length: 42 }, (_, i) => {
              const day = addDays(first, i), iso = isoDate(day), selected = value === iso;
              return <button key={iso} type="button" data-date={iso} aria-label={format(day, { year: "numeric", month: "long", day: "numeric" })} aria-pressed={selected} aria-current={iso === today ? "date" : undefined} aria-disabled={!allowed(iso)} tabIndex={focusDay === iso ? 0 : -1}
                className={`mx-auto flex h-9 w-9 items-center justify-center rounded-lg border text-[12px] tabular-nums focus-visible:outline-2 focus-visible:outline-accent ${selected ? "border-accent bg-accent text-accent-fg" : `${iso === today ? "border-border-strong" : "border-transparent"} ${day.getMonth() !== month.getMonth() ? "text-faint/60" : "text-fg"} ${i % 7 > 4 ? "bg-hover" : ""} hover:bg-active`} ${!allowed(iso) ? "cursor-not-allowed opacity-30" : ""}`}
                onFocus={() => setFocusDay(iso)} onKeyDown={(e) => moveFocus(e, day)} onClick={() => { if (allowed(iso)) void select(iso); }}>{day.getDate()}</button>;
            })}
          </div>
        </> : <>
          <p className="mb-3 px-1 text-[11.5px] text-faint">{t(boundary === "start" ? "calendar.periodStart" : "calendar.periodEnd")}</p>
          <div className={`grid gap-2 ${mode === "month" ? "grid-cols-3" : "grid-cols-2"}`}>
            {Array.from({ length: periods }, (_, index) => {
              const date = periodDate(year, index * months, months, boundary);
              const name = mode === "month" ? format(new Date(year, index, 1), { month: "short" }) : mode === "year" ? String(year) : t(`calendar.${mode}Name`, { n: index + 1 });
              return <button key={index} type="button" disabled={!allowed(date)} aria-pressed={value === date} className={`rounded-lg border px-2 py-3 text-left hover:bg-hover disabled:opacity-30 ${value === date ? "border-accent text-accent" : "border-border"}`} onClick={() => void select(date)}><span className="block text-[13px]">{name}</span><span className="mt-1 block text-[10px] text-faint">{date}</span></button>;
            })}
          </div>
        </>}
      </div>
      <div className="flex items-center gap-2 border-t border-border px-4 py-3">
        <button type="button" className="text-[12px] text-muted hover:text-fg disabled:opacity-40" disabled={!allowed(today)} onClick={() => void select(today)}>{t("calendar.today")}</button>
        {!required && value && <button type="button" className="ml-2 text-[12px] text-muted hover:text-fg" onClick={() => void select("")}>{t("calendar.clear")}</button>}
        <button type="button" className="btn btn-primary ml-auto h-7 text-[12px]" disabled={!input.trim()} onClick={applyInput}>{t("common.apply")}</button>
      </div>
    </fieldset>
  </Popover.Content>;
}
